import dayjs from "dayjs";
import type {
    CalendarItem,
    CalendarItemForm,
    CalendarItemSource,
    CalendarItemSourceValue,
    CompanyUser,
    AutomationSourceFeature,
    EventSource,
    ExternalCalendarProvider,
    RawCompanyUser,
    SharedAudience,
    TaskRepeatOption,
    TaskType,
} from "./types";

export const EVENT_SOURCE = {
    MANUAL: "manual",
    NOTICEBOARD: "noticeboard",
    EMAIL_CAMPAIGN: "emailCampaign",
    GOAL: "goal",
    HABIT: "habit",
    MOOD: "mood",
    CHECKLIST: "checklist",
    SURVEY: "survey",
    POLL: "poll",
    TEAM_GOAL: "teamGoal",
    GOOGLE: "google",
    OUTLOOK: "outlook",
    APPLE: "apple",
} as const;

export const EVENT_SOURCE_LABEL: Record<EventSource, string> = {
    [EVENT_SOURCE.MANUAL]: "Calendar Item",
    [EVENT_SOURCE.NOTICEBOARD]: "Noticeboard",
    [EVENT_SOURCE.EMAIL_CAMPAIGN]: "Email Campaign",
    [EVENT_SOURCE.GOAL]: "Personal Goal",
    [EVENT_SOURCE.HABIT]: "Habit",
    [EVENT_SOURCE.MOOD]: "Mood Tracker",
    [EVENT_SOURCE.CHECKLIST]: "Checklist",
    [EVENT_SOURCE.SURVEY]: "Survey",
    [EVENT_SOURCE.POLL]: "Poll",
    [EVENT_SOURCE.TEAM_GOAL]: "Team Goal",
    [EVENT_SOURCE.GOOGLE]: "Google Calendar",
    [EVENT_SOURCE.OUTLOOK]: "Microsoft Outlook",
    [EVENT_SOURCE.APPLE]: "Apple Calendar",
};

export const EXTERNAL_CALENDAR_PROVIDERS: ReadonlyArray<{
    provider: ExternalCalendarProvider;
    label: string;
    color: string;
    supportsInboundSync: boolean;
    supportsOutboundSync: boolean;
}> = [
    {
        provider: EVENT_SOURCE.GOOGLE,
        label: EVENT_SOURCE_LABEL[EVENT_SOURCE.GOOGLE],
        color: "#34a853",
        supportsInboundSync: true,
        supportsOutboundSync: false,
    },
    {
        provider: EVENT_SOURCE.OUTLOOK,
        label: EVENT_SOURCE_LABEL[EVENT_SOURCE.OUTLOOK],
        color: "#3b82f6",
        supportsInboundSync: true,
        supportsOutboundSync: false,
    },
    {
        provider: EVENT_SOURCE.APPLE,
        label: EVENT_SOURCE_LABEL[EVENT_SOURCE.APPLE],
        color: "#f97316",
        supportsInboundSync: true,
        supportsOutboundSync: false,
    },
];

const EXTERNAL_CALENDAR_SOURCE_KEYS = new Set<EventSource>([
    EVENT_SOURCE.GOOGLE,
    EVENT_SOURCE.OUTLOOK,
    EVENT_SOURCE.APPLE,
]);

const AUTOMATION_SOURCE_KEYS = new Set<EventSource>([
    EVENT_SOURCE.NOTICEBOARD,
    EVENT_SOURCE.EMAIL_CAMPAIGN,
    EVENT_SOURCE.GOAL,
    EVENT_SOURCE.HABIT,
    EVENT_SOURCE.MOOD,
    EVENT_SOURCE.CHECKLIST,
    EVENT_SOURCE.SURVEY,
    EVENT_SOURCE.POLL,
    EVENT_SOURCE.TEAM_GOAL,
]);

export const getCalendarItemSourceKey = (source?: CalendarItemSourceValue): EventSource | "" => {
    if (!source) return "";
    if (typeof source === "string") return source === EVENT_SOURCE.MANUAL ? "" : source;
    if (source.externalCalendar?.provider) return source.externalCalendar.provider;
    if (source.automation?.feature) return source.automation.feature;
    return "";
};

export const getCalendarItemSourceLabel = (source?: CalendarItemSourceValue, fallback = ""): string => {
    if (!source) return fallback;
    if (typeof source === "string") return EVENT_SOURCE_LABEL[source] || fallback;
    if (source.externalCalendar?.label) return source.externalCalendar.label;
    const sourceKey = getCalendarItemSourceKey(source);
    return sourceKey ? EVENT_SOURCE_LABEL[sourceKey] || fallback : fallback;
};

export const getCalendarItemSourceReferenceId = (source?: CalendarItemSourceValue): string => {
    if (!source || typeof source === "string") return "";
    return source.externalCalendar?.externalItemId || source.automation?.sourceReferenceId || source.automation?.entityUuid || "";
};

export const createCalendarItemSource = (
    sourceKey: EventSource | "" | null | undefined,
    sourceReferenceId = "",
    sourceLabel = ""
): CalendarItemSource | null => {
    if (!sourceKey || sourceKey === EVENT_SOURCE.MANUAL) return null;
    const label = sourceLabel || EVENT_SOURCE_LABEL[sourceKey] || sourceKey;

    if (EXTERNAL_CALENDAR_SOURCE_KEYS.has(sourceKey)) {
        return {
            externalCalendar: {
                provider: sourceKey as ExternalCalendarProvider,
                label,
                externalItemId: sourceReferenceId || undefined,
            },
        };
    }

    if (AUTOMATION_SOURCE_KEYS.has(sourceKey)) {
        return {
            automation: {
                feature: sourceKey as AutomationSourceFeature,
                label,
                sourceReferenceId: sourceReferenceId || undefined,
            },
        };
    }

    return null;
};

export const normaliseCalendarItemSource = (
    source?: CalendarItemSourceValue,
    sourceReferenceId = "",
    sourceLabel = ""
): CalendarItemSource | null => {
    if (!source) return null;
    if (typeof source === "string") {
        return createCalendarItemSource(source, sourceReferenceId, sourceLabel);
    }
    const sourceKey = getCalendarItemSourceKey(source);
    return createCalendarItemSource(
        sourceKey,
        getCalendarItemSourceReferenceId(source) || sourceReferenceId,
        getCalendarItemSourceLabel(source, sourceLabel)
    );
};

export const SHARED_AUDIENCE = {
    PERSONAL: "personal",
    EMPLOYEES: "employees",
    CLIENTS: "clients",
    BOTH: "both",
} as const;

export const TASK_TYPES = {
    MEETING: "meeting",
    TASK: "task",
    REMINDER: "reminder",
} as const;

export const AUTOMATION_ITEM_TYPES = {
    NOTICEBOARD: "noticeboard",
    GOAL_TRACKING: "goalTracking",
    HABIT: "habit",
    MOOD: "mood",
    CHECKLIST: "checklist",
    EMAIL_CAMPAIGN: "emailCampaign",
    SURVEY: "survey",
    POLL: "poll",
} as const;

export const TASK_TYPE_LABEL: Record<TaskType, string> = {
    [TASK_TYPES.MEETING]: "Meeting",
    [TASK_TYPES.TASK]: "Task",
    [TASK_TYPES.REMINDER]: "Reminder",
};

export const AUTOMATION_ITEM_TYPE_LABEL: Record<TaskType, string> = {
    [AUTOMATION_ITEM_TYPES.NOTICEBOARD]: "Noticeboard",
    [AUTOMATION_ITEM_TYPES.GOAL_TRACKING]: "Goal Tracking",
    [AUTOMATION_ITEM_TYPES.HABIT]: "Habit",
    [AUTOMATION_ITEM_TYPES.MOOD]: "Mood Tracker",
    [AUTOMATION_ITEM_TYPES.CHECKLIST]: "Checklist",
    [AUTOMATION_ITEM_TYPES.EMAIL_CAMPAIGN]: "Email Campaign",
    [AUTOMATION_ITEM_TYPES.SURVEY]: "Survey",
    [AUTOMATION_ITEM_TYPES.POLL]: "Poll",
};

export const TASK_PRIORITIES = {
    HIGH: "high",
    MEDIUM: "medium",
    LOW: "low",
} as const;

export const TASK_REPEAT_OPTIONS = {
    NONE: "none",
    EVERY_DAY: "everyDay",
    EVERY_WEEK: "everyWeek",
    EVERY_MONTH: "everyMonth",
    EVERY_YEAR: "everyYear",
} as const;

export const TASK_REPEAT_LABEL: Record<TaskRepeatOption, string> = {
    [TASK_REPEAT_OPTIONS.NONE]: "None",
    [TASK_REPEAT_OPTIONS.EVERY_DAY]: "Every Day",
    [TASK_REPEAT_OPTIONS.EVERY_WEEK]: "Every Week",
    [TASK_REPEAT_OPTIONS.EVERY_MONTH]: "Every Month",
    [TASK_REPEAT_OPTIONS.EVERY_YEAR]: "Every Year",
};

export const SOURCE_COLORS: Record<EventSource, string> = {
    [EVENT_SOURCE.MANUAL]: "#5b6ef5",
    [EVENT_SOURCE.NOTICEBOARD]: "#ef4444",
    [EVENT_SOURCE.EMAIL_CAMPAIGN]: "#7c3aed",
    [EVENT_SOURCE.GOAL]: "#10b981",
    [EVENT_SOURCE.HABIT]: "#84cc16",
    [EVENT_SOURCE.MOOD]: "#ec4899",
    [EVENT_SOURCE.CHECKLIST]: "#0ea5e9",
    [EVENT_SOURCE.SURVEY]: "#6366f1",
    [EVENT_SOURCE.POLL]: "#f59e0b",
    [EVENT_SOURCE.TEAM_GOAL]: "#38bdf8",
    [EVENT_SOURCE.GOOGLE]: "#34a853",
    [EVENT_SOURCE.OUTLOOK]: "#3b82f6",
    [EVENT_SOURCE.APPLE]: "#f97316",
};

export const AUDIENCE_COLORS: Record<SharedAudience, string> = {
    [SHARED_AUDIENCE.PERSONAL]: "#f472b6",
    [SHARED_AUDIENCE.EMPLOYEES]: "#5b6ef5",
    [SHARED_AUDIENCE.CLIENTS]: "#38bdf8",
    [SHARED_AUDIENCE.BOTH]: "#34d399",
};

export const TASK_TYPE_COLORS: Record<TaskType, string> = {
    [TASK_TYPES.MEETING]: "#5b6ef5",
    [TASK_TYPES.TASK]: "#f59e0b",
    [TASK_TYPES.REMINDER]: "#fb7185",
};

export const AUTOMATION_ITEM_TYPE_COLORS: Record<TaskType, string> = {
    [AUTOMATION_ITEM_TYPES.NOTICEBOARD]: "#ef4444",
    [AUTOMATION_ITEM_TYPES.GOAL_TRACKING]: "#10b981",
    [AUTOMATION_ITEM_TYPES.HABIT]: "#84cc16",
    [AUTOMATION_ITEM_TYPES.MOOD]: "#ec4899",
    [AUTOMATION_ITEM_TYPES.CHECKLIST]: "#14b8a6",
    [AUTOMATION_ITEM_TYPES.EMAIL_CAMPAIGN]: "#7c3aed",
    [AUTOMATION_ITEM_TYPES.SURVEY]: "#06b6d4",
    [AUTOMATION_ITEM_TYPES.POLL]: "#fb7185",
};

const fallbackTimezoneOptions: string[] = [
    "Africa/Abidjan",
    "Africa/Accra",
    "Africa/Addis_Ababa",
    "Africa/Algiers",
    "Africa/Cairo",
    "Africa/Casablanca",
    "Africa/Dakar",
    "Africa/Dar_es_Salaam",
    "Africa/Johannesburg",
    "Africa/Kampala",
    "Africa/Lagos",
    "Africa/Maputo",
    "Africa/Nairobi",
    "Africa/Tunis",
    "America/Anchorage",
    "America/Argentina/Buenos_Aires",
    "America/Asuncion",
    "America/Bogota",
    "America/Caracas",
    "America/Chicago",
    "America/Denver",
    "America/Detroit",
    "America/Guatemala",
    "America/Halifax",
    "America/Havana",
    "America/Jamaica",
    "America/La_Paz",
    "America/Lima",
    "America/Los_Angeles",
    "America/Mexico_City",
    "America/Montevideo",
    "America/New_York",
    "America/Phoenix",
    "America/Santiago",
    "America/Sao_Paulo",
    "America/St_Johns",
    "America/Toronto",
    "America/Vancouver",
    "Asia/Almaty",
    "Asia/Amman",
    "Asia/Baghdad",
    "Asia/Baku",
    "Asia/Bangkok",
    "Asia/Beirut",
    "Asia/Calcutta",
    "Asia/Colombo",
    "Asia/Dhaka",
    "Asia/Dubai",
    "Asia/Hong_Kong",
    "Asia/Jakarta",
    "Asia/Jerusalem",
    "Asia/Kabul",
    "Asia/Karachi",
    "Asia/Katmandu",
    "Asia/Kolkata",
    "Asia/Kuala_Lumpur",
    "Asia/Manila",
    "Asia/Riyadh",
    "Asia/Seoul",
    "Asia/Shanghai",
    "Asia/Singapore",
    "Asia/Taipei",
    "Asia/Tashkent",
    "Asia/Tbilisi",
    "Asia/Tehran",
    "Asia/Tokyo",
    "Asia/Yerevan",
    "Atlantic/Azores",
    "Atlantic/Reykjavik",
    "Australia/Adelaide",
    "Australia/Brisbane",
    "Australia/Darwin",
    "Australia/Hobart",
    "Australia/Melbourne",
    "Australia/Perth",
    "Australia/Sydney",
    "Europe/Amsterdam",
    "Europe/Athens",
    "Europe/Belgrade",
    "Europe/Berlin",
    "Europe/Brussels",
    "Europe/Bucharest",
    "Europe/Budapest",
    "Europe/Copenhagen",
    "Europe/Dublin",
    "Europe/Helsinki",
    "Europe/Istanbul",
    "Europe/Kiev",
    "Europe/Kyiv",
    "Europe/Lisbon",
    "Europe/London",
    "Europe/Madrid",
    "Europe/Moscow",
    "Europe/Oslo",
    "Europe/Paris",
    "Europe/Prague",
    "Europe/Rome",
    "Europe/Stockholm",
    "Europe/Vienna",
    "Europe/Warsaw",
    "Europe/Zurich",
    "Indian/Maldives",
    "Indian/Mauritius",
    "Pacific/Auckland",
    "Pacific/Chatham",
    "Pacific/Fiji",
    "Pacific/Guam",
    "Pacific/Honolulu",
    "Pacific/Noumea",
    "Pacific/Port_Moresby",
    "Pacific/Tahiti",
    "UTC",
];

const majorTimezoneOptionCandidates: string[] = [
    "UTC",
    "Pacific/Honolulu",
    "America/Anchorage",
    "America/Los_Angeles",
    "America/Phoenix",
    "America/Denver",
    "America/Chicago",
    "America/New_York",
    "America/Halifax",
    "America/St_Johns",
    "America/Sao_Paulo",
    "America/Argentina/Buenos_Aires",
    "Atlantic/Azores",
    "Europe/London",
    "Africa/Lagos",
    "Africa/Casablanca",
    "Europe/Paris",
    "Europe/Berlin",
    "Africa/Cairo",
    "Europe/Athens",
    "Europe/Moscow",
    "Asia/Tehran",
    "Asia/Dubai",
    "Asia/Karachi",
    "Asia/Kolkata",
    "Asia/Kathmandu",
    "Asia/Dhaka",
    "Asia/Bangkok",
    "Asia/Jakarta",
    "Asia/Shanghai",
    "Asia/Singapore",
    "Asia/Hong_Kong",
    "Asia/Tokyo",
    "Asia/Seoul",
    "Australia/Adelaide",
    "Australia/Darwin",
    "Australia/Sydney",
    "Australia/Melbourne",
    "Australia/Brisbane",
    "Pacific/Auckland",
    "Pacific/Chatham",
];

const timezoneDisplayNames: Record<string, string> = {
    "America/Buenos_Aires": "Buenos Aires, Argentina",
    "America/Argentina/Buenos_Aires": "Buenos Aires, Argentina",
    "America/Sao_Paulo": "Sao Paulo, Brazil",
    "Asia/Calcutta": "Kolkata, India",
    "Asia/Katmandu": "Kathmandu, Nepal",
    "Asia/Kolkata": "Kolkata, India",
    "Europe/Kiev": "Kyiv, Ukraine",
    "Europe/Kyiv": "Kyiv, Ukraine",
    "Pacific/Port_Moresby": "Port Moresby, Papua New Guinea",
    UTC: "UTC",
};

const timezoneSearchAliases: Record<string, string[]> = {
    "Africa/Abidjan": ["Cote d'Ivoire", "Ivory Coast", "Yamoussoukro", "GMT"],
    "Africa/Accra": ["Ghana", "Kumasi", "GMT"],
    "Africa/Addis_Ababa": ["Ethiopia"],
    "Africa/Algiers": ["Algeria"],
    "Africa/Cairo": ["Egypt"],
    "Africa/Casablanca": ["Morocco", "Rabat"],
    "Africa/Dakar": ["Senegal"],
    "Africa/Dar_es_Salaam": ["Tanzania"],
    "Africa/Johannesburg": ["South Africa", "Pretoria", "Cape Town"],
    "Africa/Kampala": ["Uganda"],
    "Africa/Lagos": ["Nigeria", "Abuja", "West Africa"],
    "Africa/Maputo": ["Mozambique"],
    "Africa/Nairobi": ["Kenya", "East Africa"],
    "Africa/Tunis": ["Tunisia"],
    "America/Anchorage": ["Alaska"],
    "America/Buenos_Aires": ["Argentina"],
    "America/Argentina/Buenos_Aires": ["Argentina"],
    "America/Asuncion": ["Paraguay"],
    "America/Bogota": ["Colombia"],
    "America/Caracas": ["Venezuela"],
    "America/Chicago": ["Central Time", "US Central", "Dallas", "Houston", "Austin", "Minneapolis"],
    "America/Denver": ["Mountain Time", "US Mountain", "Salt Lake City"],
    "America/Detroit": ["Michigan", "Eastern Time"],
    "America/Guatemala": ["Guatemala", "Central America"],
    "America/Halifax": ["Atlantic Time", "Nova Scotia"],
    "America/Havana": ["Cuba"],
    "America/Jamaica": ["Kingston"],
    "America/La_Paz": ["Bolivia"],
    "America/Lima": ["Peru"],
    "America/Los_Angeles": ["Pacific Time", "US Pacific", "San Francisco", "Seattle", "San Diego", "Vancouver"],
    "America/Mexico_City": ["Mexico", "CDMX"],
    "America/Montevideo": ["Uruguay"],
    "America/New_York": ["Eastern Time", "US Eastern", "Washington DC", "Boston", "Miami", "Atlanta", "Toronto"],
    "America/Phoenix": ["Arizona"],
    "America/Santiago": ["Chile"],
    "America/Sao_Paulo": ["Brazil", "Brasilia", "Rio de Janeiro"],
    "America/St_Johns": ["Newfoundland"],
    "America/Toronto": ["Canada Eastern", "Ontario", "Ottawa"],
    "America/Vancouver": ["Canada Pacific", "British Columbia"],
    "Asia/Almaty": ["Kazakhstan"],
    "Asia/Amman": ["Jordan"],
    "Asia/Baghdad": ["Iraq"],
    "Asia/Baku": ["Azerbaijan"],
    "Asia/Bangkok": ["Thailand", "Vietnam", "Hanoi", "Ho Chi Minh City"],
    "Asia/Beirut": ["Lebanon"],
    "Asia/Calcutta": ["India", "Kolkata", "Mumbai", "Delhi", "Bengaluru", "Chennai", "Hyderabad"],
    "Asia/Colombo": ["Sri Lanka"],
    "Asia/Dhaka": ["Bangladesh"],
    "Asia/Dubai": ["United Arab Emirates", "UAE", "Abu Dhabi"],
    "Asia/Hong_Kong": ["Hong Kong"],
    "Asia/Jakarta": ["Indonesia", "Java"],
    "Asia/Jerusalem": ["Israel", "Tel Aviv"],
    "Asia/Kabul": ["Afghanistan"],
    "Asia/Karachi": ["Pakistan", "Islamabad", "Lahore"],
    "Asia/Katmandu": ["Nepal", "Kathmandu"],
    "Asia/Kolkata": ["India", "Calcutta", "Mumbai", "Delhi", "Bengaluru", "Chennai", "Hyderabad"],
    "Asia/Kuala_Lumpur": ["Malaysia"],
    "Asia/Manila": ["Philippines"],
    "Asia/Riyadh": ["Saudi Arabia", "Jeddah"],
    "Asia/Seoul": ["South Korea"],
    "Asia/Shanghai": ["China", "Beijing", "Shenzhen", "Guangzhou"],
    "Asia/Singapore": ["Singapore"],
    "Asia/Taipei": ["Taiwan"],
    "Asia/Tashkent": ["Uzbekistan"],
    "Asia/Tbilisi": ["Georgia"],
    "Asia/Tehran": ["Iran"],
    "Asia/Tokyo": ["Japan", "Osaka", "Kyoto"],
    "Asia/Yerevan": ["Armenia"],
    "Atlantic/Azores": ["Portugal Azores"],
    "Atlantic/Reykjavik": ["Iceland"],
    "Australia/Adelaide": ["South Australia"],
    "Australia/Brisbane": ["Queensland", "Gold Coast"],
    "Australia/Darwin": ["Northern Territory"],
    "Australia/Hobart": ["Tasmania"],
    "Australia/Melbourne": ["Victoria"],
    "Australia/Perth": ["Western Australia"],
    "Australia/Sydney": ["New South Wales", "Canberra", "ACT"],
    "Europe/Amsterdam": ["Netherlands", "Holland"],
    "Europe/Athens": ["Greece"],
    "Europe/Belgrade": ["Serbia"],
    "Europe/Berlin": ["Germany", "Frankfurt", "Munich"],
    "Europe/Brussels": ["Belgium"],
    "Europe/Bucharest": ["Romania"],
    "Europe/Budapest": ["Hungary"],
    "Europe/Copenhagen": ["Denmark"],
    "Europe/Dublin": ["Ireland"],
    "Europe/Helsinki": ["Finland"],
    "Europe/Istanbul": ["Turkey", "Turkiye"],
    "Europe/Kiev": ["Ukraine", "Kyiv"],
    "Europe/Kyiv": ["Ukraine", "Kiev"],
    "Europe/Lisbon": ["Portugal"],
    "Europe/London": ["United Kingdom", "UK", "England", "Scotland", "Wales"],
    "Europe/Madrid": ["Spain", "Barcelona"],
    "Europe/Moscow": ["Russia"],
    "Europe/Oslo": ["Norway"],
    "Europe/Paris": ["France"],
    "Europe/Prague": ["Czechia", "Czech Republic"],
    "Europe/Rome": ["Italy", "Milan"],
    "Europe/Stockholm": ["Sweden"],
    "Europe/Vienna": ["Austria"],
    "Europe/Warsaw": ["Poland"],
    "Europe/Zurich": ["Switzerland", "Geneva"],
    "Indian/Maldives": ["Maldives"],
    "Indian/Mauritius": ["Mauritius"],
    "Pacific/Auckland": ["New Zealand", "Wellington"],
    "Pacific/Chatham": ["New Zealand Chatham Islands"],
    "Pacific/Fiji": ["Fiji", "Suva"],
    "Pacific/Guam": ["Guam"],
    "Pacific/Honolulu": ["Hawaii"],
    "Pacific/Noumea": ["New Caledonia"],
    "Pacific/Port_Moresby": ["Papua New Guinea"],
    "Pacific/Tahiti": ["French Polynesia"],
    UTC: ["Coordinated Universal Time", "GMT", "Zulu"],
};

const isValidTimezone = (timezone: string): boolean => {
    try {
        new Intl.DateTimeFormat("en-AU", { timeZone: timezone }).format(new Date());
        return true;
    } catch {
        return false;
    }
};

const isSelectableTimezone = (timezone: string): boolean => {
    if (timezone.startsWith("Etc/")) {
        return false;
    }
    return true;
};

export const getTimezonePlaceName = (timezone: string): string => {
    if (timezoneDisplayNames[timezone]) return timezoneDisplayNames[timezone];
    if (timezone === "UTC") return "UTC";
    return (timezone.split("/").pop() || timezone).replace(/_/g, " ");
};

export const getTimezoneSearchText = (timezone: string): string => [
    timezone,
    getTimezonePlaceName(timezone),
    ...(timezoneSearchAliases[timezone] || []),
].join(" ");

const getSupportedTimezones = (): string[] => {
    const intlWithSupportedValues = Intl as typeof Intl & {
        supportedValuesOf?: (key: "timeZone") => string[];
    };
    return typeof intlWithSupportedValues.supportedValuesOf === "function"
        ? intlWithSupportedValues.supportedValuesOf("timeZone")
        : [];
};

const buildTimezoneOptions = (): string[] => {
    const seenLabels = new Set<string>();
    return Array.from(new Set([
        ...getSupportedTimezones(),
        ...fallbackTimezoneOptions,
    ]))
        .filter(isValidTimezone)
        .filter(isSelectableTimezone)
        .filter((timezone) => {
            const label = getTimezonePlaceName(timezone);
            if (seenLabels.has(label)) return false;
            seenLabels.add(label);
            return true;
        })
        .sort((left, right) => {
            const leftName = getTimezonePlaceName(left);
            const rightName = getTimezonePlaceName(right);
            return leftName.localeCompare(rightName) || left.localeCompare(right);
        });
};

export const timezoneOptions = buildTimezoneOptions();
export const majorTimezoneOptions = majorTimezoneOptionCandidates.filter(isValidTimezone);

export const normaliseCompanyUser = (companyUser: RawCompanyUser | CompanyUser | string | null | undefined): CompanyUser => {
    if (!companyUser) {
        return {
            uuid: "",
            firstName: "",
            lastName: "",
            name: "",
            email: "",
            isClient: false,
            department: "",
            employmentPosition: "",
            profilePictureUrl: "",
        };
    }

    if (typeof companyUser === "string") {
        return {
            uuid: companyUser,
            firstName: "",
            lastName: "",
            name: companyUser,
            email: "",
            isClient: false,
            department: "",
            employmentPosition: "",
            profilePictureUrl: "",
        };
    }

    const rawUser = companyUser as RawCompanyUser & Partial<CompanyUser>;
    const uuid = rawUser.uuid || rawUser.employeeUuid || rawUser.id || rawUser.email || "";
    const firstName = rawUser.firstName || "";
    const lastName = rawUser.lastName || "";
    const name = rawUser.name || rawUser.employeeName || `${firstName} ${lastName}`.trim() || rawUser.email || "Unknown user";

    return {
        uuid,
        firstName,
        lastName,
        name,
        email: rawUser.email || rawUser.emailAddress || "",
        isClient: Boolean(rawUser.isClient),
        department: rawUser.department || "",
        employmentPosition: rawUser.employmentPosition || rawUser.position || "",
        profilePictureUrl: rawUser.profilePictureUrl || "",
    };
};

export const getCompanyUserName = (companyUser: CompanyUser): string => companyUser.name || `${companyUser.firstName || ""} ${companyUser.lastName || ""}`.trim() || companyUser.email || "Unknown user";

export const getCompanyUserInitials = (companyUser: CompanyUser): string => {
    const name = getCompanyUserName(companyUser);
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join("")
        .toUpperCase() || "U";
};

export const createCalendarItemFromForm = (form: CalendarItemForm, existingItem: CalendarItem | null, useScheduledFields: boolean): CalendarItem => {
    const startDateTime = form.allDay
        ? dayjs(`${form.startDate}T00:00`)
        : dayjs(`${form.startDate}T${form.startTime || "09:00"}`);
    const endDateTime = form.allDay
        ? dayjs(`${form.endDate}T23:59`)
        : dayjs(`${form.endDate}T${form.endTime || "10:00"}`);
    const progress = form.completed ? 100 : 0;
    const startTime = form.allDay ? "" : form.startTime;
    const endTime = form.allDay ? "" : form.endTime;
    const itemTypeUuid = form.itemTypeUuid || form.taskType || "";
    const baseItem = {
        id: existingItem?.id || "",
        itemUuid: existingItem?.itemUuid,
        source: existingItem?.source || null,
        sourceLabel: existingItem?.sourceLabel,
        title: form.title.trim(),
        description: form.description.trim(),
        allDay: Boolean(form.allDay),
        color: form.color,
        audience: form.audience,
        createdByName: existingItem?.createdByName || "",
        createdAt: existingItem?.createdAt || "",
        updatedAt: existingItem?.updatedAt || "",
        reminderOffset: form.reminderOffset || "none",
        itemTypeUuid,
        taskType: itemTypeUuid,
        priority: form.priority,
        recurring: form.repeat !== TASK_REPEAT_OPTIONS.NONE,
        sourceReferenceId: existingItem?.sourceReferenceId,
        ownerEmail: existingItem?.ownerEmail,
        automationDetail: existingItem?.automationDetail,
        meetingUrl: form.meetingUrl.trim() || existingItem?.meetingUrl,
        meeting: form.meeting,
        repeat: form.repeat,
        invites: form.invites.trim(),
        location: form.location.trim(),
        url: form.url.trim(),
        attachments: form.attachments,
    };

    if (useScheduledFields) {
        return {
            ...baseItem,
            startAt: startDateTime.toISOString(),
            endAt: endDateTime.toISOString(),
            visibility: form.audience === SHARED_AUDIENCE.PERSONAL ? "personal" : "shared",
            editable: existingItem?.editable ?? true,
            deletable: existingItem?.deletable ?? true,
            status: existingItem?.status || "active",
            visibilityRecipients: form.visibilityRecipients,
        };
    }

    return {
        ...baseItem,
        startDate: form.startDate,
        startTime,
        endDate: form.endDate,
        endTime,
        dueDate: form.startDate,
        dueTime: startTime,
        completed: Boolean(form.completed),
        status: form.completed ? "done" : "active",
        progress,
        color: form.color,
        reminderOffset: form.reminderOffset || "none",
        repeat: form.repeat || TASK_REPEAT_OPTIONS.NONE,
        audience: form.audience,
        visibilityRecipients: form.visibilityRecipients || null,
    };
};

export const formatTimezoneLabel = (timezone: string): string => {
    const offset = new Intl.DateTimeFormat("en-AU", {
        timeZone: timezone,
        timeZoneName: "shortOffset",
    }).formatToParts(new Date()).find(part => part.type === "timeZoneName");
    return `${getTimezonePlaceName(timezone)} · ${offset ? offset.value : timezone}`;
};
